import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import * as icons from "common/icons";
import { confirm } from "containers/App/_actions";
import { deleteFertiliser, submitFertiliser } from "./_actions";
import { nutrients } from "common/domain";
import numeral from "numeral";
import { fertiliserViewModel } from "common/viewModels";
import ActionLink from "components/ActionLink";
import { factoryFertiliser } from "common/domain";
import { modal } from "containers/App/_actions";
import { withAppContext } from "containers/HOC";

const FertiliserList = ({ budget, confirm, refData, deleteFertiliser, removeLinks, submitFertiliser, modal, farm }) => {
    const hasDCD = (budget.nitrateInhibitors || []).length > 0;

    const _confirmDelete = (fertiliser) => {
        deleteFertiliser(budget.farmId, budget.id, fertiliser);
    };

    const _deleteFertiliser = (fertiliser) => (e) => {
        e.preventDefault();
        confirm(`Are you sure you want to delete this fertiliser?`, () => _confirmDelete(fertiliser));
    };

    const localSubmitFertiliser = (farmId, budgetId, values) => {
        const { fertiliser, existing } = values;
        if (existing && existing.id !== fertiliser.id) {
            deleteFertiliser(farm.id, budget.id, { id: existing.id });
        } else if (values.origId !== fertiliser.id) {
            deleteFertiliser(farmId, budgetId, { id: values.origId });
        }
        const { applications = [] } = fertiliser;
        applications.forEach((app) => delete app.fertiliser);
        submitFertiliser(farmId, budgetId, fertiliser);
    };

    const editFertiliser = (fertiliser) => {
        const viewModel = {
            material: fertiliser.material,
            farm,
            budget,
            fertiliser,
            refData,
            isLastPreviousLime: false,
            customCompositions: [],
            isNew: false,
        };

        modal("FertiliserProductModal", localSubmitFertiliser, viewModel);
    };

    const addFertilser = (material, isLastPreviousLime = false) => {
        const fertiliser = factoryFertiliser();
        fertiliser.id = "NEW";

        const viewModel = {
            material,
            farm,
            budget,
            fertiliser,
            refData,
            isLastPreviousLime,
            customCompositions: [],
            isNew: true,
        };

        modal("FertiliserProductModal", localSubmitFertiliser, viewModel);
    };

    const vm = fertiliserViewModel(budget, refData).fertilisers;
    const farmNutrientTotals = { applied: 0 };

    return (
        <React.Fragment>
            {vm.filter((f) => !f.isLastPreviousLime).length === 0 && (
                <div className="Tile-body">
                    <div className="Tile-body-message">
                        <img src={icons.add} alt="Plus" />
                        <p className="lead">You do not have any fertilisers</p>
                        <ActionLink onClick={() => addFertilser("Product")} className="IconLink--arrow-plus Button Button--lg u-mt-lg" id="add-product">
                            Add product
                        </ActionLink>
                        <ActionLink onClick={() => addFertilser("Lime")} className="IconLink--arrow-plus Button Button--lg u-mt-md" id="add-lime">
                            Add lime
                        </ActionLink>
                        <ActionLink onClick={() => addFertilser("Organic")} className="IconLink--arrow-plus Button Button--lg u-mt-md" id="add-organic">
                            Add organic
                        </ActionLink>
                        <ActionLink onClick={() => addFertilser("Soluble")} className="IconLink--arrow-plus Button Button--lg u-mt-md" id="add-soluble">
                            Add soluble
                        </ActionLink>
                        {!hasDCD && (
                            <Link to={`/app/farm/${farm.id}/analysis/${budget.id}/fertiliser/dcd`} className="IconLink--arrow-plus Button Button--lg u-mt-md" id="fertiliser-add-dcd">
                                <span>Add DCD</span>
                            </Link>
                        )}
                    </div>
                </div>
            )}
            {vm.filter((f) => !f.isLastPreviousLime).length > 0 && (
                <>
                    <div className="Table u-mt-md">
                        <table>
                            <thead>
                                <tr>
                                    <th data-width="md-15 xl-15">Manufacturer/material</th>
                                    <th data-width="md-20 xl-30">Name</th>
                                    <th>Applications</th>
                                    <th data-width="md-10 xl-10">Total applied (KG)</th>
                                    {nutrients.map((n, i) => {
                                        farmNutrientTotals[n] = 0;
                                        return (
                                            <th data-width="md-5 xl-5" key={i} className="u-textCenter">
                                                {n}
                                            </th>
                                        );
                                    })}
                                    {vm.length > 0 && !removeLinks && <th className="th--shrink">&nbsp;</th>}
                                </tr>
                            </thead>
                            <tbody>
                                <React.Fragment>
                                    {vm
                                        .filter((f) => !f.isLastPreviousLime)
                                        .map((fertiliser) => {
                                            farmNutrientTotals.applied += fertiliser.total;
                                            const { applications = [] } = fertiliser;
                                            const { messages = [] } = budget;
                                            const alertIcon = messages.find((m) => m.category === "Fertiliser" && m.entityType === "Fertiliser" && m.entityId === fertiliser.id && m.severity === "Error") ? "error" : undefined;

                                            return (
                                                <tr key={fertiliser.id}>
                                                    <td>
                                                        <span className="u-flex u-flexAlignItemsCenter">
                                                            {alertIcon && (
                                                                <span className="Todo Todo--error error-anchor">
                                                                    <i className="icon icon-alert" />
                                                                </span>
                                                            )}
                                                            <img className="u-mr-xs" src={fertiliser.icon} width="35" height="35" alt={`${fertiliser.material} icon`} />
                                                            <span id={`fertiliser-manufacturer-${fertiliser.id}`}>{fertiliser.manufacturer}</span>
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {removeLinks ? (
                                                            <span>
                                                                <b>{fertiliser.displayName}</b>
                                                            </span>
                                                        ) : (
                                                            <Link id={`fertiliserid-${fertiliser.id}`} to={`/app/farm/${budget.farmId}/analysis/${budget.id}/fertiliser/applications?productId=${fertiliser.id}`}>
                                                                <span>
                                                                    <b>{fertiliser.displayName}</b>
                                                                </span>
                                                            </Link>
                                                        )}
                                                    </td>
                                                    <td className="u-textRight">{applications.length}</td>
                                                    <td className="u-textRight">
                                                        <span id={`fertiliser-amount-${fertiliser.id}`}>{fertiliser.total > 0 ? numeral(fertiliser.total).format("0,0") : "-"}</span>
                                                    </td>
                                                    {nutrients.map((n, i) => {
                                                        farmNutrientTotals[n] += fertiliser.nutrientTotals[n] || 0;
                                                        return (
                                                            <td key={i} className="u-textRight">
                                                                <span id={`fertiliser-nutrient-${n}-${fertiliser.id}`}>{fertiliser.nutrientTotals[n] ? numeral(fertiliser.nutrientTotals[n]).format("0,0") : "-"}</span>
                                                            </td>
                                                        );
                                                    })}
                                                    {!removeLinks && (
                                                        <td>
                                                            <div className="u-flex">
                                                                <ActionLink onClick={() => editFertiliser(fertiliser)} className="IconLink--edit u-font-md" id={`edit-fertiliser-${fertiliser.id}`}>
                                                                    <span></span>
                                                                </ActionLink>
                                                                <ActionLink id={`delete-fertiliser-${fertiliser.id}`} onClick={_deleteFertiliser(fertiliser)} className="IconLink--cross-circle" title="Delete"></ActionLink>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                </React.Fragment>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td>
                                        <span className="u-flex u-flexAlignItemsCenter">
                                            <span>Total</span>
                                        </span>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <th className="u-textRight">
                                        <h4 className="u-mt-0">{numeral(farmNutrientTotals.applied).format("0,0")}</h4>
                                    </th>
                                    {nutrients.map((n, i) => (
                                        <td key={i} className="u-textRight">
                                            <h4 className="u-mt-0">
                                                <span id={`fertiliser-nutrient-${n}-total`}>{farmNutrientTotals[n] ? numeral(farmNutrientTotals[n]).format("0,0") : "-"}</span>
                                            </h4>
                                        </td>
                                    ))}
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </>
            )}
        </React.Fragment>
    );
};

export default connect(withAppContext, { confirm, deleteFertiliser, modal, submitFertiliser })(FertiliserList);
